import { CartesianGrid, Line, LineChart, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { chartDomain, formatMeasure, type IndicatorSeries } from '@/lib/assessment/examTrends';
import type { Level } from '@/lib/assessment/namatAssessment';

// One indicator over the exams (#112): a point per exam, the background shaded
// by the level bands (green good, yellow acceptable, red bad). Blood pressure
// draws systolic and diastolic; the shading follows systolic and diastolic's
// limits are dashed lines.

export const BAND_FILL: Record<Level, string> = {
  good: '#22c55e',
  acceptable: '#eab308',
  bad: '#ef4444',
};
const LINE_COLORS = ['#004250', '#7c3aed'];

interface Props {
  series: IndicatorSeries;
  /** Small chart without axes for the cards. */
  sparkline?: boolean;
  height?: number;
  formatDate: (d: Date) => string;
  lineNames?: string[];
}

export function ExamTrendChart({ series, sparkline = false, height, formatDate, lineNames = [] }: Props) {
  const { min, max } = chartDomain(series);
  const data = series.points.map((p) => {
    const row: Record<string, number | string | null> = { label: formatDate(p.date) };
    p.values.forEach((v, i) => (row[`v${i}`] = v));
    return row;
  });
  const lines = Array.from({ length: series.lines }, (_, i) => i);
  const [primary, ...others] = series.bands ?? [];

  return (
    <ResponsiveContainer width="100%" height={height ?? (sparkline ? 56 : 240)}>
      <LineChart data={data} margin={sparkline ? { top: 4, right: 4, bottom: 4, left: 4 } : { top: 8, right: 16, bottom: 4, left: 0 }}>
        {(primary ?? []).map((b) => {
          const y1 = Math.max(b.from, min);
          const y2 = Math.min(b.to, max);
          return y2 > y1 ? (
            <ReferenceArea key={`${b.level}-${b.from}`} y1={y1} y2={y2} fill={BAND_FILL[b.level]} fillOpacity={0.14} stroke="none" ifOverflow="hidden" />
          ) : null;
        })}
        {!sparkline &&
          others.flatMap((bands) =>
            bands
              .filter((b) => b.from > min && b.from < max)
              .map((b) => (
                <ReferenceLine key={`d-${b.from}`} y={b.from} stroke={BAND_FILL[b.level]} strokeDasharray="4 4" ifOverflow="hidden" />
              ))
          )}
        {!sparkline && <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />}
        <XAxis dataKey="label" hide={sparkline} tick={{ fontSize: 11 }} interval="preserveStartEnd" />
        <YAxis domain={[min, max]} hide={sparkline} tick={{ fontSize: 11 }} width={40} allowDataOverflow tickFormatter={(v: number) => formatMeasure(v)} />
        {!sparkline && (
          <Tooltip
            formatter={(value: number, name: string) => [`${formatMeasure(value)} ${series.unit}`, name]}
            labelStyle={{ fontWeight: 600 }}
          />
        )}
        {lines.map((i) => (
          <Line
            key={i}
            type="monotone"
            dataKey={`v${i}`}
            name={lineNames[i] ?? ''}
            stroke={LINE_COLORS[i] ?? LINE_COLORS[0]}
            strokeWidth={sparkline ? 1.5 : 2}
            dot={{ r: sparkline ? 2 : 4 }}
            activeDot={sparkline ? false : { r: 6 }}
            isAnimationActive={false}
            connectNulls
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
